/**
 * Bluezone Recipe Service
 *
 * Greift auf die bluezoneRecipes Collection im geteilten Firebase-Projekt zu.
 * goodplace-match und goodwork-crafting nutzen dasselbe Firebase Projekt (goodwork-crafting-app).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from '@firebase/firestore';
import { db } from './firebase';
import type { BluezoneRecipe } from '../types/bluezoneTypes';

const RECIPES_COLLECTION = 'bluezoneRecipes';

/**
 * Laedt alle veroeffentlichten Rezepte
 */
export async function getPublishedRecipes(maxResults: number = 50): Promise<BluezoneRecipe[]> {
  try {
    const q = query(
      collection(db, RECIPES_COLLECTION),
      where('isPublished', '==', true),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as BluezoneRecipe[];
  } catch (error) {
    console.error('[BluezoneRecipeService] Error loading recipes:', error);
    return [];
  }
}

/**
 * Laedt ein einzelnes Rezept nach ID
 */
export async function getRecipeById(recipeId: string): Promise<BluezoneRecipe | null> {
  try {
    const docRef = doc(db, RECIPES_COLLECTION, recipeId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as BluezoneRecipe;
  } catch (error) {
    console.error('[BluezoneRecipeService] Error loading recipe:', error);
    return null;
  }
}

/**
 * Sucht Rezepte nach Titel (fuer Admin-Picker)
 */
export async function searchRecipes(searchTerm: string): Promise<BluezoneRecipe[]> {
  try {
    // Firestore unterstuetzt keine Volltextsuche, daher laden wir alle
    // veroeffentlichten Rezepte und filtern clientseitig
    const recipes = await getPublishedRecipes(100);

    if (!searchTerm.trim()) {
      return recipes;
    }

    const term = searchTerm.toLowerCase();
    return recipes.filter(
      (recipe) =>
        recipe.title.toLowerCase().includes(term) ||
        recipe.subtitle.toLowerCase().includes(term) ||
        recipe.regionLabel.toLowerCase().includes(term)
    );
  } catch (error) {
    console.error('[BluezoneRecipeService] Error searching recipes:', error);
    return [];
  }
}
